// Pure, framework-free logic for the create wizard. Kept separate from the
// step components so business rules (variant generation, limits, checklist,
// resumability) can be unit tested without rendering React.
import {
  coerceNullableNumber,
  createEmptyMediaItem,
  mapEnrichmentToForm,
  toInputValue,
} from "../../utils/catalogProductStudioShared";
import {
  buildProductColors,
  isColorDimensionName,
  normalizeDimensionKey,
} from "../../utils/catalogColorCover";
import { mapCatalogMediaSaveItems } from "../../utils/catalogMediaCover";
import { CATALOG_MEDIA_ROLE_VALUES } from "../../utils/catalogProductStudioShared";

// ─── Ported unchanged from the old single-page create flow ────────────────
// (SimpleCatalogCreateFlow in CatalogProductStudio.js). Business behavior —
// generation order, SKU-suggestion shape, alias tables — must stay byte
// identical to what the backend already expects.

export const SIZE_PRESETS = {
  footwear: ["35", "36", "37", "38", "39", "40", "41", "42"],
  apparel: ["XS", "S", "M", "L", "XL", "XXL", "3XL"],
  apparelNumeric: ["36", "38", "40", "42", "44", "46", "48", "50"],
  apparelJeans: ["26", "27", "28", "29", "30", "31", "32", "33", "34", "35", "36"],
};

// Sizes a new product starts with; the rest of the preset stays one tap away.
export const DEFAULT_SIZE_SELECTION = {
  footwear: ["36", "37", "38", "39", "40"],
  apparel: ["S", "M", "L", "XL", "XXL"],
};

// Textile can be sized with letters (XS … XXL), numbers (36 … 50) or jeans waist sizes (26 … 36).
export const APPAREL_SIZE_SYSTEMS = {
  letters: "apparel",
  numeric: "apparelNumeric",
  jeans: "apparelJeans",
};

export const resolveSizePresetKey = (sizePresetKey, apparelSizeSystem) =>
  sizePresetKey === "apparel"
    ? APPAREL_SIZE_SYSTEMS[apparelSizeSystem] || APPAREL_SIZE_SYSTEMS.letters
    : sizePresetKey;

/**
 * Resumed drafts open on the system whose preset covers the most saved sizes;
 * ties (e.g. only "36", shared by numbers and jeans) keep the earlier system.
 */
export const inferApparelSizeSystem = (sizes) => {
  const values = sizes || [];
  let bestSystem = "letters";
  let bestCount = 0;
  Object.entries(APPAREL_SIZE_SYSTEMS).forEach(([system, presetKey]) => {
    const count = values.filter((size) => SIZE_PRESETS[presetKey].includes(size)).length;
    if (count > bestCount) {
      bestSystem = system;
      bestCount = count;
    }
  });
  return bestSystem;
};

const COLOR_ALIASES = {
  blk: "Black",
  blck: "Black",
  blackk: "Black",
  wht: "White",
  whit: "White",
  gry: "Grey",
  gray: "Grey",
  gr: "Grey",
  slvr: "Silver",
  silvr: "Silver",
  glod: "Gold",
  beig: "Beige",
  brwn: "Brown",
  brn: "Brown",
  nav: "Navy",
  navyblue: "Navy Blue",
  darkblue: "Dark Blue",
  lightblue: "Light Blue",
};

const SIZE_ALIASES = {
  EXTRASMALL: "XS",
  EXTRASM: "XS",
  XSMALL: "XS",
  SMALL: "S",
  MEDIUM: "M",
  LARGE: "L",
  EXTRALARGE: "XL",
  XLARGE: "XL",
  DOUBLEEXTRALARGE: "XXL",
  DOUBLEXL: "XXL",
  XXXL: "3XL",
  TRIPLEXL: "3XL",
  ONE: "ONE SIZE",
  ONESIZE: "ONE SIZE",
  OS: "ONE SIZE",
  EU35: "35",
  EU36: "36",
  EU37: "37",
  EU38: "38",
  EU39: "39",
  EU40: "40",
  EU41: "41",
  EU42: "42",
};

export const normalizeSimpleOptionValues = (values) =>
  Array.from(
    new Set(
      (values || [])
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );

export const normalizeLookupKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

export const normalizeColorLabel = (value) => {
  const cleaned = String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
  const aliasKey = cleaned.toLowerCase().replace(/\s+/g, "");
  return COLOR_ALIASES[aliasKey] || cleaned;
};

export const normalizeSizeLabel = (value) => {
  const cleaned = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
  return SIZE_ALIASES[cleaned] || cleaned;
};

export const inferOptionGuidance = (t, { productFamily, merchandisingTypeLabel }) => {
  const family = normalizeLookupKey(productFamily);
  const type = normalizeLookupKey(merchandisingTypeLabel);
  const isFootwearType =
    family === "shoes" ||
    type.includes("heel") ||
    type.includes("sneaker") ||
    type.includes("boot") ||
    type.includes("loafer") ||
    type.includes("sandal") ||
    type.includes("shoe");
  const isApparelType =
    family === "textile" ||
    type.includes("hoodie") ||
    type.includes("shirt") ||
    type.includes("tee") ||
    type.includes("dress") ||
    type.includes("jacket") ||
    type.includes("coat") ||
    type.includes("pants") ||
    type.includes("trouser") ||
    type.includes("textile");
  const isAccessoryType =
    family === "accessories" ||
    type.includes("bag") ||
    type.includes("wallet") ||
    type.includes("belt") ||
    type.includes("accessor");

  if (isFootwearType) {
    return {
      optionMode: "size-and-color",
      sizePresetKey: "footwear",
      headline: t("catalogProductStudio.guidanceFootwearHeadline"),
      copy: t("catalogProductStudio.guidanceFootwearCopy"),
    };
  }

  if (isApparelType) {
    return {
      optionMode: "size-and-color",
      sizePresetKey: "apparel",
      headline: t("catalogProductStudio.guidanceApparelHeadline"),
      copy: t("catalogProductStudio.guidanceApparelCopy"),
    };
  }

  if (isAccessoryType) {
    return {
      optionMode: "color-only",
      sizePresetKey: null,
      headline: t("catalogProductStudio.guidanceAccessoriesHeadline"),
      copy: t("catalogProductStudio.guidanceAccessoriesCopy"),
    };
  }

  return {
    optionMode: "size-and-color",
    sizePresetKey: inferSizePresetKey({ productFamily, merchandisingTypeLabel }),
    headline: t("catalogProductStudio.guidanceDefaultHeadline"),
    copy: t("catalogProductStudio.guidanceDefaultCopy"),
  };
};

export const inferSizePresetKey = ({ productFamily, merchandisingTypeLabel }) => {
  const family = normalizeLookupKey(productFamily);
  const type = normalizeLookupKey(merchandisingTypeLabel);

  if (family === "accessories") {
    if (type.includes("bag") || type.includes("wallet") || type.includes("belt")) {
      return null;
    }
    return "apparel";
  }

  if (family === "shoes") {
    return "footwear";
  }

  if (family === "textile") {
    if (type.includes("shoe") || type.includes("heel") || type.includes("boot")) {
      return "footwear";
    }
    return "apparel";
  }

  return null;
};

export const findColorSuggestion = (value, colorDictionary) => {
  const normalizedValue = normalizeLookupKey(value);
  if (!normalizedValue) {
    return null;
  }

  return (
    colorDictionary.find((entry) => normalizeLookupKey(entry) === normalizedValue) ||
    colorDictionary.find((entry) =>
      normalizeLookupKey(entry).startsWith(normalizedValue)
    ) ||
    null
  );
};

export const buildSimpleOptionDimensions = ({ hasOptions, sizes, colors, t }) => {
  if (!hasOptions) {
    return [];
  }

  const normalizedColors = Array.from(
    new Set(normalizeSimpleOptionValues(colors).map(normalizeColorLabel))
  );
  const normalizedSizes = Array.from(
    new Set(normalizeSimpleOptionValues(sizes).map(normalizeSizeLabel))
  );
  const dimensions = [];

  if (normalizedColors.length) {
    dimensions.push({
      name: t("catalogProductStudio.colorOption"),
      values: normalizedColors,
    });
  }

  if (normalizedSizes.length) {
    dimensions.push({
      name: t("catalogProductStudio.sizeOption"),
      values: normalizedSizes,
    });
  }

  return dimensions;
};

export const buildVariantCombinationKey = (optionSelections) => {
  if (!optionSelections.length) {
    return "default";
  }

  return optionSelections
    .map((selection) => selection.value.trim().toLowerCase().replace(/\s+/g, "-"))
    .join("__");
};

export const buildSimpleVariantRows = ({ title, hasOptions, sizes, colors, t }) => {
  const dimensions = buildSimpleOptionDimensions({ hasOptions, sizes, colors, t });

  if (!dimensions.length) {
    return [
      {
        key: "default",
        label: title?.trim() || t("catalogProductStudio.defaultProduct"),
        optionSelections: [],
      },
    ];
  }

  const combinations = [];
  const walk = (depth, currentSelections) => {
    if (depth >= dimensions.length) {
      const values = currentSelections.map((entry) => entry.value);
      combinations.push({
        key: buildVariantCombinationKey(currentSelections),
        label: values.join(" / "),
        optionSelections: currentSelections,
      });
      return;
    }

    dimensions[depth].values.forEach((value) => {
      walk(depth + 1, [
        ...currentSelections,
        {
          name: dimensions[depth].name,
          value,
        },
      ]);
    });
  };

  walk(0, []);
  return combinations;
};

export const deriveWorkspaceVariantKey = (variant) => {
  const selections = (variant.optionValues || []).map((optionValue) => ({
    name: optionValue.optionName,
    value: optionValue.value,
  }));

  return buildVariantCombinationKey(selections);
};

const CSS_COLOR_LITERAL_PATTERN = /^(#([0-9a-f]{3}|[0-9a-f]{6,8})|(rgb|rgba|hsl|hsla)\()/i;

let cssColorProbeElement = null;

const isValidCssColor = (candidate) => {
  if (!candidate || typeof document === "undefined") {
    return false;
  }

  if (!cssColorProbeElement) {
    cssColorProbeElement = document.createElement("div");
  }

  cssColorProbeElement.style.color = "";
  cssColorProbeElement.style.color = candidate;
  const resolved = cssColorProbeElement.style.color;
  return Boolean(resolved) && resolved !== "inherit";
};

// Albanian colour names used in the live catalog. CMS colour records often
// have no hexCode, and these names are not CSS colours, so without this a
// "Kafe" (brown) swatch would be painted with an arbitrary colour.
const ALBANIAN_COLOR_SWATCHES = {
  zi: "#111111",
  zeze: "#111111",
  bardhe: "#f5f5f5",
  kafe: "#6b4226",
  kafeeerrete: "#4a2c1a",
  taba: "#8b4a2b",
  krem: "#efe4cf",
  bezhe: "#d8c3a5",
  bordo: "#7a1f2b",
  kuqe: "#d62839",
  hiri: "#8a8f98",
  gri: "#8a8f98",
  blu: "#1d4ed8",
  kaltert: "#60a5fa",
  gjelbert: "#2f6f33",
  jeshile: "#2f6f33",
  verdhe: "#eab308",
  portokalli: "#f97316",
  roze: "#e58fb5",
  pembe: "#e58fb5",
  vjollce: "#7c3aed",
  lejla: "#a78bfa",
  ari: "#d4af37",
  argjend: "#c0c0c0",
};

// Neutral two-tone swatch for a colour we genuinely can't resolve — never a
// random hue that could look like a different colour.
const UNKNOWN_COLOR_SWATCH = "linear-gradient(135deg, #cbd5e1 0 50%, #64748b 50% 100%)";

const lookupAlbanianSwatch = (value) =>
  ALBANIAN_COLOR_SWATCHES[normalizeDimensionKey(value)] || null;

const buildColorSwatchCandidates = (label, colorRecord = null) => {
  const sourceValues = [
    colorRecord?.hexCode,
    label,
    colorRecord?.colorName,
    colorRecord?.name,
    colorRecord?.englishName,
    colorRecord?.albanianName,
    colorRecord?.turkishName,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  const candidates = [];

  sourceValues.forEach((value) => {
    if (CSS_COLOR_LITERAL_PATTERN.test(value)) {
      candidates.push(value);
    }

    const lower = value.toLowerCase();
    candidates.push(lower);
    candidates.push(lower.replace(/\s+/g, ""));
    lower.split(/\s+/).forEach((part) => {
      if (part) {
        candidates.push(part);
      }
    });
  });

  return Array.from(new Set(candidates));
};

/** CSS colour (or a stable hashed HSL fallback) to paint a colour's swatch dot. */
export const resolveColorSwatchBackground = (label, colorRecord = null) => {
  const candidates = buildColorSwatchCandidates(label, colorRecord);

  for (const candidate of candidates) {
    if (CSS_COLOR_LITERAL_PATTERN.test(candidate)) {
      return candidate;
    }

    if (isValidCssColor(candidate)) {
      return candidate;
    }
  }

  const localized = [label, colorRecord?.albanianName, colorRecord?.colorName]
    .map(lookupAlbanianSwatch)
    .find(Boolean);
  return localized || UNKNOWN_COLOR_SWATCH;
};

// ─── Backend limits, mirrored for client-side warnings ─────────────────────
// Keep these equal to the DTO/planner limits so the wizard warns before the
// API ever has to reject a request:
// - ProductOptionDimensionDto.values: ArrayMaxSize(50)
// - SaveCatalogProductStructureDto.optionDimensions: ArrayMaxSize(3)
// - products-catalog-plan.builder HARD_VARIANT_LIMIT / SOFT_VARIANT_WARNING_LIMIT
export const MAX_DIMENSION_VALUES = 50;
export const MAX_DIMENSIONS = 3;
export const HARD_VARIANT_LIMIT = 250;
export const SOFT_VARIANT_WARNING_LIMIT = 40;
export const MAX_MEDIA_ITEMS = 30;

// ─── New wizard-only helpers ────────────────────────────────────────────────

const SIZE_DIMENSION_KEYS = [
  "size",
  "sizes",
  "number",
  "numbers",
  // Albanian — the live catalog names this dimension "Madhësia".
  "madhesia",
  "madhesite",
  "numri",
  "numrat",
];

/** Accent/locale-aware size-dimension matcher, mirroring isColorDimensionName. */
export const isSizeDimensionName = (...names) =>
  names.some((name) => SIZE_DIMENSION_KEYS.includes(normalizeDimensionKey(name)));

/** Sub-range of an ordered preset (e.g. footwear sizes 36..40), inclusive both ends. */
export const buildSizeRangeSelection = (presetValues, startValue, endValue) => {
  const startIndex = presetValues.indexOf(startValue);
  const endIndex = presetValues.indexOf(endValue);
  if (startIndex === -1 || endIndex === -1) {
    return [];
  }

  const [from, to] = startIndex <= endIndex ? [startIndex, endIndex] : [endIndex, startIndex];
  return presetValues.slice(from, to + 1);
};

/**
 * "Zgjidh shpejt" options for an ordered size preset: the full preset plus
 * every contiguous window of 5 and 4 sizes (e.g. 36–40, 37–41, 36–39).
 */
export const buildQuickSizeRanges = (presetValues) => {
  if (!presetValues || presetValues.length < 3) {
    return [];
  }

  const ranges = [];
  const seen = new Set();
  const push = (from, to) => {
    const key = `${from}|${to}`;
    if (from === to || seen.has(key)) return;
    seen.add(key);
    ranges.push({ key, from, to, label: `${from} – ${to}` });
  };

  push(presetValues[0], presetValues[presetValues.length - 1]);
  [5, 4].forEach((windowSize) => {
    for (let start = 0; start + windowSize <= presetValues.length; start += 1) {
      push(presetValues[start], presetValues[start + windowSize - 1]);
    }
  });

  return ranges;
};

/** Preset sizes in preset order (35, 36 … / XS, S …), then custom sizes as added. */
export const orderSizes = (sizes, presetValues = []) => {
  const preset = sizes
    .filter((size) => presetValues.includes(size))
    .sort((left, right) => presetValues.indexOf(left) - presetValues.indexOf(right));
  const custom = sizes.filter((size) => !presetValues.includes(size));
  return [...preset, ...custom];
};

/** "€89.00" for a valid amount, "" for blank/invalid input (never invents 0). */
export const formatEuro = (value) => {
  const amount = coerceNullableNumber(value);
  return amount == null ? "" : `€${amount.toFixed(2)}`;
};

/** Size value of a generated row, or null when the product has no sizes. */
export const getRowSizeValue = (row) =>
  row.optionSelections.find((selection) => isSizeDimensionName(selection.name))?.value ?? null;

/** Stock total per colour group, for the preview's "Përmbledhje e stokut". */
export const buildStockByColor = (colorGroups, quantityByRowKey) =>
  colorGroups.map((group) => ({
    color: group.color,
    total: group.rows.reduce(
      (sum, row) => sum + Math.max(0, Number(quantityByRowKey[row.key] || 0) || 0),
      0
    ),
  }));

const WEBSHOP_TEXT_FIELDS = [
  "productStory",
  "material",
  "composition",
  "careInstructions",
  "fitNotes",
  "sizeGuideNotes",
  "stylingNotes",
  "highlightsText",
  "badgesText",
];

/** How many "Detaje për webshop" fields have content. */
export const countFilledWebshopFields = (enrichment) =>
  WEBSHOP_TEXT_FIELDS.filter((field) => String(enrichment?.[field] || "").trim()).length +
  ((enrichment?.detailSections || []).some(
    (section) => String(section.title || "").trim() && String(section.body || "").trim()
  )
    ? 1
    : 0);

/** Which live-summary sentence Step 2 should show. */
export const describeVariantMatrix = (colorCount, sizeCount) => {
  if (colorCount > 0 && sizeCount > 0) return "both";
  if (colorCount > 0) return "colorOnly";
  if (sizeCount > 0) return "sizeOnly";
  return "none";
};

/**
 * Reorders a media item within its own colour group only, so dragging a
 * "Zi" photo up or down can never jump it across into "Krem"'s section.
 * Swaps by id rather than raw index, since the flat array interleaves every
 * colour's items together.
 */
export const moveMediaItemWithinGroup = (items, id, direction) => {
  const groupKey = items.find((item) => item.id === id)?.colorLabel ?? null;
  if (groupKey === undefined) {
    return items;
  }

  const groupIndices = items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => (item.colorLabel ?? null) === groupKey)
    .map(({ index }) => index);

  const sourcePosition = groupIndices.findIndex((index) => items[index].id === id);
  const targetPosition = sourcePosition + direction;
  if (sourcePosition === -1 || targetPosition < 0 || targetPosition >= groupIndices.length) {
    return items;
  }

  const sourceIndex = groupIndices[sourcePosition];
  const targetIndex = groupIndices[targetPosition];
  const next = [...items];
  [next[sourceIndex], next[targetIndex]] = [next[targetIndex], next[sourceIndex]];
  return next;
};

/**
 * Groups generated variant rows by their colour value, in first-seen order.
 * Falls back to a single ungrouped section (`color: null`) for size-only or
 * no-variant products, so Step 3 always has at least one section to render.
 */
export const groupVariantRowsByColor = (rows) => {
  const order = [];
  const byColor = new Map();

  rows.forEach((row) => {
    const colorSelection = row.optionSelections.find((selection) =>
      isColorDimensionName(selection.name)
    );
    const key = colorSelection ? colorSelection.value : "__no_color__";

    if (!byColor.has(key)) {
      byColor.set(key, []);
      order.push(key);
    }
    byColor.get(key).push(row);
  });

  return order.map((key) => ({
    color: key === "__no_color__" ? null : key,
    rows: byColor.get(key),
  }));
};

/**
 * Hard requirements must all be empty before "Krijo & publiko"/"Ruaj si
 * draft" can submit. Soft warnings never block — images and stock are not
 * backend publish requirements (see buildCatalogPublishingBlockingIssues on
 * the API, which never checks either).
 */
export const buildPublishChecklist = ({
  title,
  price,
  variantCount,
  totalStock,
  mediaCount,
  colors,
  mediaItems,
  enrichment,
  isOverVariantLimit = false,
}) => {
  const hard = [];
  if (!String(title || "").trim()) {
    hard.push("name");
  }
  if (price === undefined || price === null || price === "" || Number.isNaN(Number(price))) {
    hard.push("price");
  }
  // Over HARD_VARIANT_LIMIT the API's planner rejects the structure save, so
  // this blocks both actions instead of failing mid-sequence.
  if (!variantCount || variantCount < 1 || isOverVariantLimit) {
    hard.push("variants");
  }

  const soft = [];
  if (!mediaCount) {
    soft.push("noImages");
  }
  if (!totalStock) {
    soft.push("zeroStock");
  }
  // Colour option-value ids don't exist yet at review time — saveStructure
  // hasn't run — so this matches on the wizard's own colour LABEL instead of
  // colorCoverOptionValueIds (which only gets populated for real at submit).
  if ((colors || []).length > 1) {
    const coveredLabels = new Set(
      (mediaItems || [])
        .filter((item) => item.isColorCover && item.colorLabel)
        .map((item) => normalizeLookupKey(item.colorLabel))
    );
    if (colors.some((color) => !coveredLabels.has(normalizeLookupKey(color)))) {
      soft.push("missingColorCover");
    }
  }
  const hasWebshopDetails = Boolean(
    String(enrichment?.productStory || "").trim() ||
      String(enrichment?.highlightsText || "").trim() ||
      String(enrichment?.material || "").trim()
  );
  if (!hasWebshopDetails) {
    soft.push("incompleteWebshop");
  }

  return { hard, soft };
};

// ─── Submit-stage bookkeeping (for the "which stage failed" retry banner) ──

export const WIZARD_STAGES = {
  DRAFT: "draft",
  CORE: "core",
  STRUCTURE: "structure",
  VARIANTS: "variants",
  MEDIA: "media",
  INVENTORY: "inventory",
  PUBLISHING: "publishing",
};

const STAGE_TRANSLATION_KEYS = {
  [WIZARD_STAGES.DRAFT]: "catalogProductStudio.wizardStageFailedDraft",
  [WIZARD_STAGES.CORE]: "catalogProductStudio.wizardStageFailedCore",
  [WIZARD_STAGES.STRUCTURE]: "catalogProductStudio.wizardStageFailedStructure",
  [WIZARD_STAGES.VARIANTS]: "catalogProductStudio.wizardStageFailedVariants",
  [WIZARD_STAGES.MEDIA]: "catalogProductStudio.wizardStageFailedMedia",
  [WIZARD_STAGES.INVENTORY]: "catalogProductStudio.wizardStageFailedInventory",
  [WIZARD_STAGES.PUBLISHING]: "catalogProductStudio.wizardStageFailedPublishing",
};

export const stageFailureTranslationKey = (stage) =>
  STAGE_TRANSLATION_KEYS[stage] || "catalogProductStudio.wizardStageFailedGeneric";

// ─── Payload builders (pure — the orchestrator just awaits the mutations) ──

export const buildStructurePayload = ({ productMode, optionDimensions, price, compareAtPrice, cost }) => ({
  productMode,
  generationMode: "cartesian",
  optionDimensions: optionDimensions.length
    ? optionDimensions.map((dimension) => ({
        name: dimension.name,
        values: dimension.values,
      }))
    : undefined,
  pricing: {
    price: coerceNullableNumber(price),
    compareAtPrice: coerceNullableNumber(compareAtPrice),
    // ProductCatalogPricingPreviewDto.cost — applied to every new variant.
    cost: coerceNullableNumber(cost),
  },
  inventory: {
    // Create mode always uses aggregate stock — serialized inventory is not
    // supported by this flow's updateInventory call (the API 400s otherwise).
    mode: "aggregate",
  },
});

export const buildVariantOverridesPayload = ({ persistedVariants, rowOverrides }) =>
  persistedVariants
    .map((variant) => {
      const rowKey = deriveWorkspaceVariantKey(variant);
      const overrides = rowOverrides[rowKey] || {};
      const payload = { id: variant.id };

      if (overrides.sku && overrides.sku.trim()) {
        payload.sku = overrides.sku.trim();
      }
      if (overrides.barcode && overrides.barcode.trim()) {
        payload.barcode = overrides.barcode.trim();
      }
      const overridePrice = coerceNullableNumber(overrides.price);
      if (overridePrice != null) {
        payload.price = overridePrice;
      }

      return payload;
    })
    .filter((item) => Object.keys(item).length > 1);

export const buildInventoryPayload = ({ persistedVariants, locationId, quantityByRowKey }) => ({
  locationId: Number(locationId),
  items: persistedVariants.map((variant) => {
    const rowKey = deriveWorkspaceVariantKey(variant);
    return {
      variantId: variant.id,
      quantity: Math.max(0, Number(quantityByRowKey[rowKey] || 0) || 0),
    };
  }),
});

/**
 * Resolves each wizard media item's Step-3 colour LABEL (`colorLabel`,
 * `isColorCover`) into the real `variantIds` / `colorCoverOptionValueIds`
 * the API needs, using the colour list `buildProductColors` derives from a
 * just-saved structure response (or a resumed workspace). Colour option-value
 * ids never exist before that structure save, so this step must run after
 * it — never before.
 */
export const resolveMediaColorCovers = (mediaItems, resolvedColors) => {
  const colorsByLabel = new Map(
    (resolvedColors || []).map((color) => [normalizeLookupKey(color.value), color])
  );

  return (mediaItems || []).map((item) => {
    if (!item.colorLabel) {
      return item;
    }

    const color = colorsByLabel.get(normalizeLookupKey(item.colorLabel));
    if (!color) {
      return { ...item, variantIds: [], colorCoverOptionValueIds: [] };
    }

    return {
      ...item,
      variantIds: color.variantIds,
      colorCoverOptionValueIds: item.isColorCover ? [color.optionValueId] : [],
    };
  });
};

/**
 * Media items are scoped to a colour's variants at upload time (Step 3), so
 * the backend's colour-cover eligibility check (an image must belong to a
 * variant of the colour it covers) is satisfied without extra plumbing, and
 * an image uploaded under "Zi" can never be offered as "Krem"'s cover.
 */
export const buildMediaPayload = (mediaItems) =>
  mapCatalogMediaSaveItems(mediaItems, (item, position) => ({
    storageKey: String(item.storageKey || "").trim() || undefined,
    sourceUrl:
      !String(item.storageKey || "").trim() && String(item.sourceUrl || "").trim()
        ? String(item.sourceUrl || "").trim()
        : undefined,
    altText: String(item.altText || "").trim() || undefined,
    role: CATALOG_MEDIA_ROLE_VALUES.includes(item.role) ? item.role : "GALLERY",
    isPrimary: Boolean(item.isCover),
    position,
    variantIds: item.variantIds || [],
    colorCoverOptionValueIds: item.colorCoverOptionValueIds || [],
    thumbnailKey: String(item.thumbnailKey || "").trim() || undefined,
    cardKey: String(item.cardKey || "").trim() || undefined,
    fullKey: String(item.fullKey || "").trim() || undefined,
  })).filter((item) => item.storageKey || item.sourceUrl);

// ─── Resume-after-reload hydration ─────────────────────────────────────────

/**
 * Rebuilds the wizard's local state from a previously-created (possibly
 * partially saved) product, so retrying after a reload — not just an
 * in-memory retry — resumes real work instead of starting blank.
 */
export const hydrateWizardStateFromWorkspace = (workspace) => {
  if (!workspace) {
    return null;
  }

  const product = workspace.product || {};
  const options = workspace.options || [];
  const variants = workspace.variants || [];
  const media = workspace.media || [];

  const colorOption = options.find((option) =>
    isColorDimensionName(option.key, option.name)
  );
  const sizeOption = options.find((option) => isSizeDimensionName(option.key, option.name));

  const liveVariants = variants.filter((variant) => variant.status !== "archived");
  const firstVariant = liveVariants[0] || variants[0] || null;

  const quantityByRowKey = {};
  variants.forEach((variant) => {
    const rowKey = deriveWorkspaceVariantKey(variant);
    const onHand = variant.inventory?.summary?.onHand;
    quantityByRowKey[rowKey] = String(onHand || 0);
  });

  // Reverse-map each persisted media item's real variantIds back to the
  // wizard's label-based colour grouping, so resumed photos land back in
  // the right Step 3 colour section instead of an "unassigned" bucket.
  const resolvedColors = buildProductColors(options, variants);
  const colorByVariantId = new Map();
  resolvedColors.forEach((color) => {
    (color.variantIds || []).forEach((variantId) => {
      colorByVariantId.set(variantId, color);
    });
  });
  const colorLabelForMediaItem = (item) => {
    const matchedVariantId = (item.variantIds || []).find((variantId) =>
      colorByVariantId.has(variantId)
    );
    return matchedVariantId ? colorByVariantId.get(matchedVariantId).value : null;
  };

  return {
    core: {
      title: product.title || "",
      description: product.description || "",
      price: toInputValue(firstVariant?.price),
      compareAtPrice: toInputValue(firstVariant?.compareAtPrice),
      cost: toInputValue(firstVariant?.cost),
      brandId: product.brandId === null || product.brandId === undefined ? "" : String(product.brandId),
      productFamily: product.productFamily || "shoes",
      merchandisingTypeId:
        product.merchandisingTypeId === null || product.merchandisingTypeId === undefined
          ? ""
          : String(product.merchandisingTypeId),
      slug: product.slug || "",
      baseSku: product.baseSku || "",
      metaCatalogEnabled: Boolean(product.metaCatalogEnabled),
      enrichment: mapEnrichmentToForm(product.enrichment),
    },
    publishStatus: product.status === "active" ? "active" : "draft",
    hasOptions: Boolean(colorOption || sizeOption),
    colors: colorOption ? colorOption.values.map((value) => value.value) : [],
    sizes: sizeOption ? sizeOption.values.map((value) => value.value) : [],
    mediaItems: media.length
      ? media
          .slice()
          .sort((a, b) => (a.position || 0) - (b.position || 0))
          .map((item) => {
            const colorLabel = colorLabelForMediaItem(item);
            const color = colorLabel
              ? resolvedColors.find((entry) => entry.value === colorLabel)
              : null;

            return {
              ...createEmptyMediaItem(),
              id: `resumed-${item.id}`,
              storageKey: item.storageKey || "",
              sourceUrl: item.sourceUrl || "",
              altText: item.altText || "",
              role: item.role || "GALLERY",
              isCover: Boolean(item.isPrimary),
              variantIds: item.variantIds || [],
              colorCoverOptionValueIds: item.colorCoverOptionValueIds || [],
              thumbnailKey: item.thumbnailKey || "",
              cardKey: item.cardKey || "",
              fullKey: item.fullKey || "",
              colorLabel,
              isColorCover: Boolean(
                color && (item.colorCoverOptionValueIds || []).includes(color.optionValueId)
              ),
            };
          })
      : [],
    quantityByRowKey,
  };
};
